import { Image } from "@mui/icons-material";
import {
  Avatar,
  Box,
  CircularProgress,
  Paper,
  Popover,
  Stack,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import apiService, { BaseURL } from "../../../service/Api";
import { tokens } from "../../../theme";
import AssignModal from "./AssignModal";
import {
  AssignableUser,
  ApiResponse,
  SiteVisitAssignee,
  SiteVisitListResponse,
  UsersResponse,
  Venue,
} from "./venues-widget.types";
import UserAvatarRow from "./UserAvatarRow";

type SiteVisitData = {
  assignees: SiteVisitAssignee[];
  scheduled_date_time: string | null;
};

type DraggingAssignee = {
  user: SiteVisitAssignee;
  fromVenueId: number;
};

export default function VenuesWidget() {
  const [venues, setVenues] = useState<Venue[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeIndex, setActiveIndex] = useState(0);

  const [assignableUsers, setAssignableUsers] = useState<AssignableUser[]>([]);
  const [usersLoading, setUsersLoading] = useState(false);

  const [draggingUser, setDraggingUser] = useState<AssignableUser | null>(null);
  const assigningUserRef = useRef<AssignableUser | null>(null);

  const draggingAssigneeRef = useRef<DraggingAssignee | null>(null);
  const [isDraggingAssignee, setIsDraggingAssignee] = useState(false);

  const [dragOverVenueId, setDragOverVenueId] = useState<number | null>(null);
  const [dragOverUserRow, setDragOverUserRow] = useState(false);

  const [popoverAnchor, setPopoverAnchor] = useState<HTMLElement | null>(null);
  const [popoverUsers, setPopoverUsers] = useState<SiteVisitAssignee[]>([]);

  const [modalOpen, setModalOpen] = useState(false);
  const [selectedVenue, setSelectedVenue] = useState<Venue | null>(null);
  const [assignedUsersByVenueId, setAssignedUsersByVenueId] = useState<
    Record<number, SiteVisitData>
  >({});

  const navigate = useNavigate();
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const fetchVenues = async () => {
      try {
        const response = await apiService.get<ApiResponse>(
          "/api/management/dashboard/venues-statistics/",
        );
        setVenues(response.data?.lowest10 || []);
      } catch (error) {
        console.error("Failed to fetch venues", error);
        setVenues([]);
      } finally {
        setLoading(false);
      }
    };
    fetchVenues();
  }, []);

  useEffect(() => {
    const fetchAssignedSiteVisits = async () => {
      try {
        const response = await apiService.get<SiteVisitListResponse>(
          "/api/venues/site-visit/",
          { page: 1, page_size: 1000, status: "assigned" },
        );
        const assignedMap: Record<number, SiteVisitData> = {};
        response.data.results.forEach((visit) => {
          if (
            visit.status === "assigned" &&
            visit.venue?.id &&
            visit.assignees?.length
          ) {
            assignedMap[visit.venue.id] = {
              assignees: visit.assignees,
              scheduled_date_time: visit.scheduled_date_time,
            };
          }
        });
        setAssignedUsersByVenueId(assignedMap);
      } catch (error) {
        console.error("Failed to fetch assigned site visits", error);
      }
    };
    fetchAssignedSiteVisits();
  }, []);

  useEffect(() => {
    const fetchAssignableUsers = async () => {
      try {
        setUsersLoading(true);
        const response = await apiService.get<UsersResponse>(
          "/api/authentication/users/",
          { role: ["admin", "dev", "sales"], page: 1, page_size: 1000 },
        );
        setAssignableUsers(response.data.results || []);
      } catch (error) {
        console.error("Failed to fetch assignable users", error);
        setAssignableUsers([]);
      } finally {
        setUsersLoading(false);
      }
    };
    fetchAssignableUsers();
  }, []);

  const getMediaUrl = (path?: string | null) => {
    if (!path) return undefined;
    if (path.startsWith("http://") || path.startsWith("https://")) return path;
    const cleanBaseUrl = BaseURL.replace(/\/$/, "");
    const cleanPath = path.startsWith("/") ? path : `/${path}`;
    return `${cleanBaseUrl}${cleanPath}`;
  };

  const formatDate = (dateStr: string | null | undefined) => {
    if (!dateStr) return null;
    return new Date(dateStr).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  const handleScroll = () => {
    if (scrollContainerRef.current) {
      const scrollLeft = scrollContainerRef.current.scrollLeft;
      const width = scrollContainerRef.current.clientWidth;
      const newIndex = Math.round(scrollLeft / width);
      if (newIndex !== activeIndex) setActiveIndex(newIndex);
    }
  };

  const scrollTo = (index: number) => {
    if (scrollContainerRef.current) {
      const width = scrollContainerRef.current.clientWidth;
      scrollContainerRef.current.scrollTo({
        left: width * index,
        behavior: "smooth",
      });
      setActiveIndex(index);
    }
  };

  const handleDragStart = (user: AssignableUser) => {
    setDraggingUser(user);
  };
  const handleAssigneeDragStart = (
    user: SiteVisitAssignee,
    fromVenueId: number,
  ) => {
    draggingAssigneeRef.current = { user, fromVenueId };
    setIsDraggingAssignee(true);
  };

  const handleDragOver = (e: React.DragEvent, venueId: number) => {
    e.preventDefault();
    setDragOverVenueId(venueId);
  };

  const handleDragLeave = () => {
    setDragOverVenueId(null);
  };

  const handleDragEnd = () => {
    setDraggingUser(null);
    draggingAssigneeRef.current = null;
    setIsDraggingAssignee(false);
    setDragOverVenueId(null);
    setDragOverUserRow(false);
  };

  const handleDrop = (e: React.DragEvent, venue: Venue) => {
    e.preventDefault();
    setDragOverVenueId(null);

    if (draggingAssigneeRef.current) {
      const { user, fromVenueId } = draggingAssigneeRef.current;
      if (fromVenueId !== venue.id) {
        handleMoveAssignee(user, fromVenueId, venue);
      }
      draggingAssigneeRef.current = null;
      setIsDraggingAssignee(false);
      return;
    }

    if (!draggingUser) return;
    assigningUserRef.current = draggingUser;

    const hasExistingSiteVisit =
      (assignedUsersByVenueId[venue.id]?.assignees ?? []).length > 0;
    if (hasExistingSiteVisit) {
      handleConfirmAssign("", venue);
    } else {
      setSelectedVenue(venue);
      setModalOpen(true);
    }
  };

  const handleDropOnUserRow = async () => {
    setDragOverUserRow(false);
    const dragging = draggingAssigneeRef.current;
    if (!dragging) return;

    const { user, fromVenueId } = dragging;
    draggingAssigneeRef.current = null;
    setIsDraggingAssignee(false);

    await handleRemoveAssignee(user.id, fromVenueId);
  };

  const handleRemoveAssignee = async (userId: number, venueId: number) => {
    const res = await apiService.get<SiteVisitListResponse>(
      "/api/venues/site-visit/",
      { venue: venueId, status: "assigned" },
    );
    const siteVisit = res.data.results?.[0];
    if (!siteVisit) return;

    const remainingIds = siteVisit.assignees
      .map((u) => u.id)
      .filter((id) => id !== userId);

    await apiService.patch(`/api/venues/site-visit/${siteVisit.id}/`, {
      assignee_ids: remainingIds,
    });

    setAssignedUsersByVenueId((prev) => {
      const existing = prev[venueId];
      if (!existing) return prev;
      const updated = existing.assignees.filter((u) => u.id !== userId);
      if (updated.length === 0) {
        const next = { ...prev };
        delete next[venueId];
        return next;
      }
      return { ...prev, [venueId]: { ...existing, assignees: updated } };
    });
  };

  const handleMoveAssignee = async (
    user: SiteVisitAssignee,
    fromVenueId: number,
    toVenue: Venue,
  ) => {
    await handleRemoveAssignee(user.id, fromVenueId);

    assigningUserRef.current = {
      id: user.id,
      first_name: user.first_name,
      last_name: user.last_name,
      email: user.email,
      role: user.role,
      profile_picture_url: user.profile_picture_url,
    };

    const hasExisting =
      (assignedUsersByVenueId[toVenue.id]?.assignees ?? []).length > 0;
    if (hasExisting) {
      handleConfirmAssign("", toVenue);
    } else {
      setSelectedVenue(toVenue);
      setModalOpen(true);
    }
  };

  const handleConfirmAssign = async (date: string, venue?: Venue) => {
    const user = assigningUserRef.current;
    const targetVenue = venue ?? selectedVenue;
    if (!targetVenue || !user) return;

    const res = await apiService.get<SiteVisitListResponse>(
      "/api/venues/site-visit/",
      { venue: targetVenue.id, status: "assigned" },
    );

    const siteVisit = res.data.results?.[0];
    const currentAssigned = siteVisit?.assignees ?? [];

    const allIds = [...currentAssigned.map((u) => u.id), user.id].filter(
      (id, i, arr) => arr.indexOf(id) === i,
    );

    if (siteVisit) {
      await apiService.patch(`/api/venues/site-visit/${siteVisit.id}/`, {
        assignee_ids: allIds,
        status: "assigned",
      });
    } else {
      await apiService.post(`/api/venues/site-visit/`, {
        venue_id: targetVenue.id,
        scheduled_date_time: new Date(date).toISOString(),
        assignee_ids: allIds,
        status: "assigned",
      });
    }

    setAssignedUsersByVenueId((prev) => {
      const existing = prev[targetVenue.id];
      const existingAssignees = existing?.assignees ?? [];
      const alreadyAdded = existingAssignees.some((u) => u.id === user.id);
      if (alreadyAdded) return prev;
      return {
        ...prev,
        [targetVenue.id]: {
          assignees: [...existingAssignees, user as SiteVisitAssignee],
          scheduled_date_time:
            siteVisit?.scheduled_date_time ?? new Date(date).toISOString(),
        },
      };
    });

    setModalOpen(false);
    setSelectedVenue(null);
    assigningUserRef.current = null;
  };

  const slide1 = venues.slice(0, 4);
  const slide2 = venues.slice(4, 8);
  const slide3 = venues.slice(8, 10);
  const featuredVenue = slide1[0];

  const dots = [];
  if (slide1.length > 0) dots.push(0);
  if (slide2.length > 0) dots.push(1);
  if (slide3.length > 0) dots.push(2);

  const renderAssignedAvatars = (
    assignedUsers: SiteVisitAssignee[],
    venueId: number,
  ) => (
    <Box
      sx={{ display: "flex", alignItems: "center", overflow: "visible", pr: 1 }}
      onClick={(e) => {
        e.stopPropagation();
        setPopoverUsers(assignedUsers);
        setPopoverAnchor(e.currentTarget);
      }}
    >
      {assignedUsers.slice(0, 3).map((u, i) => (
        <Tooltip
          key={u.id}
          title={`${u.first_name} ${u.last_name}`.trim() || u.email}
        >
          <Avatar
            draggable
            onDragStart={(e) => {
              e.stopPropagation();
              handleAssigneeDragStart(u, venueId);
            }}
            src={getMediaUrl(u.profile_picture_url)}
            sx={{
              width: 28,
              height: 28,
              bgcolor: "#22c55e",
              fontSize: "0.65rem",
              fontWeight: 700,
              border: `2px solid ${colors.primary[900]}`,
              ml: i === 0 ? 0 : -1,
              zIndex: 3 - i,
              cursor: "grab",
              "&:active": { cursor: "grabbing" },
            }}
          >
            {`${u.first_name?.[0] ?? ""}${u.last_name?.[0] ?? ""}`.toUpperCase() ||
              "?"}
          </Avatar>
        </Tooltip>
      ))}
      {assignedUsers.length > 3 && (
        <Avatar
          sx={{
            width: 28,
            height: 28,
            fontSize: "0.6rem",
            ml: -1,
            zIndex: 0,
            bgcolor: "grey.500",
          }}
        >
          +{assignedUsers.length - 3}
        </Avatar>
      )}
    </Box>
  );

  const renderListItem = (venue: Venue, rank: number) => {
    const percent = Math.round(venue.completeness_score ?? 0);
    const isOver = dragOverVenueId === venue.id;
    const siteVisitData = assignedUsersByVenueId[venue.id];
    const assignedUsers = siteVisitData?.assignees ?? [];
    const scheduledDate = siteVisitData?.scheduled_date_time;
    const isAssigned = assignedUsers.length > 0;

    return (
      <Box
        key={venue.id}
        draggable={false}
        onDragOver={(e) => handleDragOver(e, venue.id)}
        onDragLeave={handleDragLeave}
        onDrop={(e) => handleDrop(e, venue)}
        onClick={() => navigate(`/venues/view/${venue.id}`)}
        sx={{
          display: "flex",
          alignItems: "center",
          border: `1px solid ${isOver ? colors.primary[400] : colors.primary[800]}`,
          borderRadius: "16px",
          p: 1,
          cursor: draggingUser || isDraggingAssignee ? "copy" : "pointer",
          transition: "all 0.2s ease",
          backgroundColor: isOver ? `${colors.primary[400]}22` : "transparent",
          transform: isOver ? "scale(1.01)" : "scale(1)",
          "&:hover": {
            backgroundColor:
              draggingUser || isDraggingAssignee
                ? `${colors.primary[400]}22`
                : colors.primary[800],
            transform:
              draggingUser || isDraggingAssignee
                ? "scale(1.01)"
                : "translateY(-2px)",
          },
        }}
      >
        <Typography
          fontWeight={800}
          color={colors.primary[400]}
          sx={{ width: 40, textAlign: "center", fontSize: "2.2rem" }}
        >
          {rank}
        </Typography>

        <Box sx={{ flexGrow: 1, ml: 1, minWidth: 0 }}>
          <Typography
            variant="body2"
            fontWeight={600}
            color={colors.primary[700]}
            noWrap
          >
            {venue.name}
          </Typography>
          <Typography
            variant="caption"
            color={colors.primary[300]}
            noWrap
            display="block"
          >
            {venue.address_one || "No address"}
          </Typography>
          {isAssigned && scheduledDate && (
            <Typography
              variant="caption"
              color={colors.primary[500]}
              noWrap
              display="block"
            >
              Site Visit: {formatDate(scheduledDate)}
            </Typography>
          )}
        </Box>

        {isAssigned ? (
          renderAssignedAvatars(assignedUsers, venue.id)
        ) : (
          <Box sx={{ position: "relative", display: "inline-flex" }}>
            <CircularProgress
              variant="determinate"
              value={100}
              sx={{ color: colors.primary[800] }}
              size={40}
              thickness={4}
            />
            <CircularProgress
              variant="determinate"
              value={percent}
              sx={{ color: "#F57C00", position: "absolute", left: 0 }}
              size={40}
              thickness={4}
            />
            <Box
              sx={{
                top: 0,
                left: 0,
                bottom: 0,
                right: 0,
                position: "absolute",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Typography
                variant="caption"
                component="div"
                color={colors.primary[700]}
                sx={{ fontSize: "0.6rem", fontWeight: "bold" }}
              >
                {percent}%
              </Typography>
            </Box>
          </Box>
        )}
      </Box>
    );
  };

  const renderFeaturedCard = (venue: Venue) => {
    const isOver = dragOverVenueId === venue.id;
    const siteVisitData = assignedUsersByVenueId[venue.id];
    const assignedUsers = siteVisitData?.assignees ?? [];
    const scheduledDate = siteVisitData?.scheduled_date_time;
    const isAssigned = assignedUsers.length > 0;

    return (
      <Box
        onDragOver={(e) => handleDragOver(e, venue.id)}
        onDragLeave={handleDragLeave}
        onDrop={(e) => handleDrop(e, venue)}
        onClick={() => navigate(`/venues/view/${venue.id}`)}
        sx={{
          position: "relative",
          borderRadius: "16px",
          overflow: "hidden",
          height: 120,
          mb: 2,
          cursor: draggingUser || isDraggingAssignee ? "copy" : "pointer",
          transition: "transform 0.3s ease, box-shadow 0.3s ease",
          outline: isOver ? `3px solid ${colors.primary[400]}` : "none",
          transform: isOver ? "scale(1.02)" : "scale(1)",
          "&:hover": { transform: "scale(1.02)" },
        }}
      >
        <Box
          sx={{
            width: "100%",
            height: "100%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            background: `linear-gradient(135deg, ${colors.primary[50]} 0%, ${colors.primary[400]} 100%)`,
            color: "white",
          }}
        >
          <Image sx={{ fontSize: 40, opacity: 0.7 }} />
        </Box>

        <Box
          sx={{
            position: "absolute",
            inset: 0,
            background:
              "linear-gradient(to right, rgba(0,0,0,0.75), rgba(0,0,0,0.25))",
          }}
        />

        <Box
          sx={{
            position: "absolute",
            inset: 0,
            p: 2,
            display: "flex",
            flexDirection: "column",
            justifyContent: "space-between",
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "flex-start",
            }}
          >
            {isAssigned ? (
              renderAssignedAvatars(assignedUsers, venue.id)
            ) : (
              <Box
                sx={{
                  bgcolor: "#F57C00",
                  borderRadius: "50%",
                  width: 36,
                  height: 36,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <Typography variant="caption" fontWeight="bold" color="white">
                  {Math.round(venue.completeness_score ?? 0)}%
                </Typography>
              </Box>
            )}

            {isAssigned && scheduledDate && (
              <Box
                sx={{
                  bgcolor: "rgba(0,0,0,0.45)",
                  borderRadius: "8px",
                  px: 1,
                  py: 0.25,
                }}
              >
                <Typography
                  variant="caption"
                  fontWeight={600}
                  color="white"
                  sx={{ fontSize: "0.65rem" }}
                >
                  Site Visit: {formatDate(scheduledDate)}
                </Typography>
              </Box>
            )}
          </Box>

          <Box>
            <Typography variant="subtitle2" fontWeight="bold" color="white">
              {venue.name}
            </Typography>
            <Typography variant="caption" sx={{ opacity: 0.8, color: "white" }}>
              {venue.address_one || "No address"}
            </Typography>
          </Box>
        </Box>

        {isOver && (
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              bgcolor: `${colors.primary[400]}44`,
              backdropFilter: "blur(2px)",
            }}
          >
            <Typography fontWeight={700} color="white" fontSize="0.9rem">
              Drop to assign
            </Typography>
          </Box>
        )}
      </Box>
    );
  };

  return (
    <>
      <Paper
        elevation={0}
        onDragEnd={handleDragEnd}
        sx={{
          borderRadius: "24px",
          p: 2,
          height: "100%",
          backgroundColor: colors.primary[900],
          display: "flex",
          flexDirection: "column",
        }}
      >
        <Typography
          variant="subtitle1"
          fontWeight={800}
          color={colors.primary[700]}
          sx={{ display: "block", mb: 1 }}
        >
          VENUES NEEDING ATTENTION
        </Typography>

        <UserAvatarRow
          users={assignableUsers}
          loading={usersLoading}
          onDragStart={handleDragStart}
          isDraggingAssignee={isDraggingAssignee}
          dragOverUserRow={dragOverUserRow}
          onDragOver={() => setDragOverUserRow(true)}
          onDragLeave={() => setDragOverUserRow(false)}
          onDrop={handleDropOnUserRow}
        />

        {loading ? (
          <Box
            sx={{
              flexGrow: 1,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <CircularProgress size={26} sx={{ color: colors.primary[400] }} />
          </Box>
        ) : venues.length === 0 ? (
          <Box
            sx={{
              flexGrow: 1,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Typography variant="body2" color={colors.primary[300]}>
              No venues found
            </Typography>
          </Box>
        ) : (
          <>
            <Box
              ref={scrollContainerRef}
              onScroll={handleScroll}
              sx={{
                display: "flex",
                flexGrow: 1,
                overflowX: "auto",
                scrollSnapType: "x mandatory",
                scrollbarWidth: "none",
                "&::-webkit-scrollbar": { display: "none" },
              }}
            >
              <Box
                sx={{
                  minWidth: "100%",
                  scrollSnapAlign: "start",
                  display: "flex",
                  flexDirection: "column",
                  pr: slide2.length > 0 ? 2 : 0,
                }}
              >
                {featuredVenue && renderFeaturedCard(featuredVenue)}
                <Stack spacing={2}>
                  {slide1
                    .slice(1)
                    .map((venue, index) => renderListItem(venue, index + 2))}
                </Stack>
              </Box>

              {slide2.length > 0 && (
                <Box
                  sx={{
                    minWidth: "100%",
                    scrollSnapAlign: "start",
                    display: "flex",
                    flexDirection: "column",
                    pr: slide3.length > 0 ? 2 : 0,
                  }}
                >
                  <Stack spacing={2}>
                    {slide2.map((venue, index) =>
                      renderListItem(venue, index + 5),
                    )}
                  </Stack>
                </Box>
              )}

              {slide3.length > 0 && (
                <Box
                  sx={{
                    minWidth: "100%",
                    scrollSnapAlign: "start",
                    display: "flex",
                    flexDirection: "column",
                  }}
                >
                  <Stack spacing={2}>
                    {slide3.map((venue, index) =>
                      renderListItem(venue, index + 9),
                    )}
                  </Stack>
                </Box>
              )}
            </Box>

            {dots.length > 1 && (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  mt: 3,
                  gap: 0.5,
                }}
              >
                {dots.map((index) => (
                  <Box
                    key={index}
                    onClick={() => scrollTo(index)}
                    sx={{
                      width: activeIndex === index ? 16 : 6,
                      height: 6,
                      borderRadius: 3,
                      backgroundColor:
                        activeIndex === index
                          ? colors.primary[400]
                          : colors.primary[50],
                      cursor: "pointer",
                      transition: "all 0.3s ease",
                    }}
                  />
                ))}
              </Box>
            )}
          </>
        )}
      </Paper>

      <AssignModal
        open={modalOpen}
        venue={selectedVenue}
        user={assigningUserRef.current}
        alreadyAssigned={
          selectedVenue
            ? (assignedUsersByVenueId[selectedVenue.id]?.assignees ?? [])
            : []
        }
        onClose={() => {
          setModalOpen(false);
          setSelectedVenue(null);
          assigningUserRef.current = null;
        }}
        onConfirm={handleConfirmAssign}
      />

      <Popover
        open={Boolean(popoverAnchor)}
        anchorEl={popoverAnchor}
        onClose={() => setPopoverAnchor(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        PaperProps={{ sx: { borderRadius: 2, p: 0.4, minWidth: 180 } }}
      >
        {popoverUsers.map((u) => (
          <Box
            key={u.id}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              px: 1,
              py: 0.5,
              borderRadius: 1,
              "&:hover": { bgcolor: "action.hover" },
            }}
          >
            <Avatar
              src={getMediaUrl(u.profile_picture_url)}
              sx={{
                width: 28,
                height: 28,
                bgcolor: "#22c55e",
                fontSize: "0.65rem",
              }}
            >
              {`${u.first_name?.[0] ?? ""}${u.last_name?.[0] ?? ""}`.toUpperCase() ||
                "?"}
            </Avatar>
            <Box>
              <Typography variant="body2" fontWeight={600} lineHeight={1.2}>
                {`${u.first_name} ${u.last_name}`.trim()}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {u.email}
              </Typography>
            </Box>
          </Box>
        ))}
      </Popover>
    </>
  );
}
